import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, distinctUntilChanged, takeUntil } from 'rxjs';

import { Loggable } from 'app/utils/logging/loggable';
import {
  IncomingMessage,
  IncomingMessageType,
  OutgoingMessageType,
  SpeakingStatusMessage,
} from 'messages/websocket.messages';

import { actions, socialAppSelectors } from '../../state/app';
import { MessageRecord, WebsocketService } from '../websocket.service';

@Injectable({
  providedIn: 'root',
})
export class ActiveSpeakerService extends Loggable {
  // TODO: this never completes
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  public messageHistory: MessageRecord[] = [];
  private activeSpeakers: string[] = [];
  private activePresentersSpeakers: string[] = [];

  private presentersIds: string[] = [];

  public constructor(
    private store: Store,
    private websocketService: WebsocketService,
  ) {
    super();
    this.setDisplayName('ActiveSpeakerService');

    this.store
      .select(socialAppSelectors.getActiveSpeakers)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((activeSpeakers) => (this.activeSpeakers = activeSpeakers));

    this.store
      .select(socialAppSelectors.getPresenterIds)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((presenterIds) => {
        this.presentersIds = presenterIds;
      });

    this.store
      .select(socialAppSelectors.getActivePresentationSpeakers)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((activePresentersSpeakers) => (this.activePresentersSpeakers = activePresentersSpeakers));

    this.websocketService.messageHistory$
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((message) => {
        this.handleWebsocketMessage(message.msg as IncomingMessage);
      });
  }

  public sendActiveSpeakerMessage(speaking: boolean, crossRoom: boolean, userId: string, isPresenter?: boolean) {
    try {
      const msg: SpeakingStatusMessage = {
        t: OutgoingMessageType.AS,
        speaking,
        crossRoom,
      };

      if (speaking) {
        this.handleAddSpeaker(userId, isPresenter);
      } else {
        this.handleRemoveSpeaker(userId, isPresenter);
      }

      this.websocketService.sendMessage(msg);
    } catch (e) {
      this.error('Failed to get active speakers data', e);
    }
  }

  private handleWebsocketMessage(msg: IncomingMessage): void {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.NS: {
        if (msg.userId) {
          if (msg.speaking && !this.activeSpeakers.includes(msg.userId)) {
            this.handleAddSpeaker(msg.userId);
          } else {
            this.handleRemoveSpeaker(msg.userId);
          }
        }
        return;
      }
      case IncomingMessageType.LEFT: {
        if (msg.userId) {
          this.handleRemoveSpeaker(msg.userId);
        }
        return;
      }
    }
  }

  private handleAddSpeaker(userId: string, isPresenter?: boolean): void {
    if (isPresenter || this.presentersIds.includes(userId)) {
      //Add user always on the top of the array, case user already in array, move it to the top.
      this.activePresentersSpeakers = [userId, ...this.activePresentersSpeakers.filter((id) => id !== userId)];
      this.store.dispatch(
        actions.UPDATE_ACTIVE_PRESENTERS_SPEAKERS({ activePresentersSpeakers: this.activePresentersSpeakers }),
      );
    } else {
      this.activeSpeakers = [...this.activeSpeakers, userId];
      this.store.dispatch(actions.UPDATE_ACTIVE_SPEAKERS({ activeSpeakers: this.activeSpeakers }));
    }
  }

  private handleRemoveSpeaker(userId: string, isPresenter?: boolean): void {
    if (isPresenter || this.presentersIds.includes(userId)) {
      const newActiveSpeakers = this.activePresentersSpeakers.filter((activeUser) => activeUser !== userId);
      this.activePresentersSpeakers = newActiveSpeakers;
      this.store.dispatch(
        actions.UPDATE_ACTIVE_PRESENTERS_SPEAKERS({ activePresentersSpeakers: this.activePresentersSpeakers }),
      );
    } else {
      const newActiveSpeakers = this.activeSpeakers.filter((activeUser) => activeUser !== userId);
      this.activeSpeakers = newActiveSpeakers;
      this.store.dispatch(actions.UPDATE_ACTIVE_SPEAKERS({ activeSpeakers: this.activeSpeakers }));
    }
  }

  public updateUserActiveSpeakerState(isSpeaking: boolean): void {
    this.store.dispatch(actions.UPDATE_USER_IS_SPEAKING({ isSpeaking }));
  }
}
